/**
 * Top-level ErrorBoundary — React mount/render 에러 잡아서 fallback UI 표시.
 *
 * main.tsx 에서 <App /> 을 감쌈. Phase 1 검증 게이트의 "흰 화면 방지" 핵심.
 */
import { Component, type ReactNode } from "react";
import i18n from "../../i18n";
import styles from "./ErrorBoundary.module.css";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string }): void {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info.componentStack);
    // TODO Phase 10: send to error tracking (Sentry / 자체 BE)
  }

  reset = () => {
    this.setState({ error: null });
  };

  reload = () => {
    window.location.reload();
  };

  override render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className={styles.fallback}>
        <div className={styles.icon}>⚠️</div>
        <h2 className={styles.title}>{i18n.t("error.boundaryTitle")}</h2>
        {/* 줄바꿈은 문구 안(\n)에 있다 — 끊는 자리가 언어마다 달라 <br /> 을 고정하지 않는다. */}
        <p className={styles.message}>
          {i18n.t("error.boundaryBody").split("\n").map((line, i) => (
            <span key={i}>
              {i > 0 && <br />}
              {line}
            </span>
          ))}
        </p>
        {import.meta.env.DEV && (
          <pre className={styles.errorDetail}>
            {this.state.error.name}: {this.state.error.message}
            {"\n"}
            {this.state.error.stack?.split("\n").slice(0, 5).join("\n")}
          </pre>
        )}
        <div className={styles.actions}>
          <button className={styles.btn} onClick={this.reset}>{i18n.t("action.retry")}</button>
          <button className={styles.btnPrimary} onClick={this.reload}>{i18n.t("action.reload")}</button>
        </div>
      </div>
    );
  }
}
